/**
 * Leases API
 * RTK Query endpoints for lease management
 */

import { baseApi } from "./baseApi";

/**
 * Lease interface
 */
export interface Lease {
	id: string;
	organization_id?: string;
	organization_name?: string;
	property_id: string;
	property_name?: string;
	property_address?: string;
	tenant_id: string;
	tenant_name?: string;
	tenant_email?: string;
	lease_code?: string; // Auto-generated lease code (e.g., LC-2025-0001)
	start_date: string;
	end_date: string;
	monthly_rent: number;
	security_deposit?: number;
	status: "draft" | "active" | "expired" | "terminated" | "renewed";
	terms?: string;
	renewal_deadline?: string;
	documents?: string[]; // List of Cloudinary URLs for lease documents
	signed_agreement_url?: string; // Signed agreement document URL
	signed_at?: string; // Timestamp when agreement was signed
	created_at: string;
	updated_at: string;
}

/**
 * Lease list response interface
 */
export interface LeaseListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Lease[];
}

/**
 * Create/Update lease request interface
 */
export interface LeaseData {
	organization_id?: string; // Optional - backend sets from user context
	property_id: string;
	tenant_id: string;
	start_date: string;
	end_date: string;
	monthly_rent: number;
	security_deposit?: number;
	status?: "draft" | "active" | "expired" | "terminated" | "renewed";
	terms?: string;
	renewal_deadline?: string;
	document_files?: string[]; // Base64 encoded document files for upload
}

/**
 * Leases API endpoints
 */
export const leasesApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all leases (with filters, search, pagination)
		 */
		listLeases: builder.query<
			LeaseListResponse,
			{
				page?: number;
				page_size?: number;
				status?: string;
				tenant?: string;
				property?: string;
				search?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.tenant) queryParams.append("tenant", params.tenant);
				if (params.property) queryParams.append("property", params.property);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				const queryString = queryParams.toString();
				return {
					url: queryString ? `/leases/?${queryString}` : `/leases/`,
					method: "GET",
				};
			},
			transformResponse: (response: {
				count: number;
				next: string | null;
				previous: string | null;
				results: any[];
			}) => {
				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					results:
						response.results?.map((lease: any) => ({
							...lease,
							// Convert dates to ISO strings if needed
							start_date:
								typeof lease.start_date === "string"
									? lease.start_date
									: new Date(lease.start_date).toISOString(),
							end_date:
								typeof lease.end_date === "string"
									? lease.end_date
									: new Date(lease.end_date).toISOString(),
							renewal_deadline: lease.renewal_deadline
								? typeof lease.renewal_deadline === "string"
									? lease.renewal_deadline
									: new Date(lease.renewal_deadline).toISOString()
								: undefined,
							created_at:
								typeof lease.created_at === "string"
									? lease.created_at
									: new Date(lease.created_at).toISOString(),
							updated_at:
								typeof lease.updated_at === "string"
									? lease.updated_at
									: new Date(lease.updated_at).toISOString(),
						})) || [],
				};
			},
			providesTags: ["Leases"],
		}),

		/**
		 * Get lease details by ID
		 */
		getLease: builder.query<Lease, string>({
			query: (id) => ({
				url: `/leases/${id}`,
				method: "GET",
			}),
			transformResponse: (response: any) => {
				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					// Convert dates to ISO strings if needed
					start_date:
						typeof response.start_date === "string"
							? response.start_date
							: new Date(response.start_date).toISOString(),
					end_date:
						typeof response.end_date === "string"
							? response.end_date
							: new Date(response.end_date).toISOString(),
					renewal_deadline: response.renewal_deadline
						? typeof response.renewal_deadline === "string"
							? response.renewal_deadline
							: new Date(response.renewal_deadline).toISOString()
						: undefined,
					created_at:
						typeof response.created_at === "string"
							? response.created_at
							: new Date(response.created_at).toISOString(),
					updated_at:
						typeof response.updated_at === "string"
							? response.updated_at
							: new Date(response.updated_at).toISOString(),
				};
			},
			providesTags: (result, error, id) => [{ type: "Leases", id }],
		}),

		/**
		 * Get current tenant's active lease
		 * Note: FastAPI doesn't have a dedicated endpoint, so we filter by tenant and status
		 */
		getMyLease: builder.query<Lease | null, void>({
			query: () => ({
				url: "/leases/?status=active&page_size=1",
				method: "GET",
			}),
			transformResponse: (response: LeaseListResponse) => {
				// Return first active lease (should be the tenant's lease)
				return response.results && response.results.length > 0
					? response.results[0]
					: null;
			},
			providesTags: ["Leases"],
		}),

		/**
		 * Get all active leases
		 * Note: FastAPI doesn't have a dedicated endpoint, so we filter by status
		 */
		getActiveLeases: builder.query<Lease[], void>({
			query: () => ({
				url: "/leases/?status=active",
				method: "GET",
			}),
			transformResponse: (response: LeaseListResponse) =>
				response.results || [],
			providesTags: ["Leases"],
		}),

		/**
		 * Create a new lease (admin only)
		 * Extended timeout for document uploads
		 */
		createLease: builder.mutation<Lease, LeaseData>({
			query: (data) => ({
				url: "/leases/",
				method: "POST",
				body: data,
				timeout: 120000, // 2 minutes for document uploads
			}),
			invalidatesTags: ["Leases"],
		}),

		/**
		 * Update a lease (admin only)
		 * Extended timeout for document uploads
		 */
		updateLease: builder.mutation<
			Lease,
			{ id: string; data: Partial<LeaseData> }
		>({
			query: ({ id, data }) => ({
				url: `/leases/${id}`,
				method: "PUT",
				body: data,
				timeout: 120000, // 2 minutes for document uploads
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Leases", id },
				"Leases",
			],
		}),

		/**
		 * Partially update a lease (admin only)
		 * Extended timeout for document uploads
		 */
		patchLease: builder.mutation<
			Lease,
			{ id: string; data: Partial<LeaseData> }
		>({
			query: ({ id, data }) => ({
				url: `/leases/${id}`,
				method: "PATCH",
				body: data,
				timeout: 120000, // 2 minutes for document uploads
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Leases", id },
				"Leases",
			],
		}),

		/**
		 * Delete a lease (admin only)
		 */
		deleteLease: builder.mutation<void, string>({
			query: (id) => ({
				url: `/leases/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Leases"],
		}),

		/**
		 * Send bulk notifications to tenants (admin only)
		 */
		bulkNotifyLeases: builder.mutation<
			{
				success: boolean;
				message: string;
				success_count: number;
				failed_count: number;
				failed_leases: Array<{ lease_id: string; error: string }>;
			},
			{
				lease_ids: string[];
				notification_type: string;
				custom_message?: string;
			}
		>({
			query: (data) => ({
				url: "/leases/bulk-notify",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Leases"],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListLeasesQuery,
	useLazyListLeasesQuery,
	useGetLeaseQuery,
	useLazyGetLeaseQuery,
	useGetMyLeaseQuery,
	useLazyGetMyLeaseQuery,
	useGetActiveLeasesQuery,
	useLazyGetActiveLeasesQuery,
	useCreateLeaseMutation,
	useUpdateLeaseMutation,
	usePatchLeaseMutation,
	useDeleteLeaseMutation,
	useBulkNotifyLeasesMutation,
} = leasesApi;
